import { GetListResponse, HttpError, useDeleteMany, useList, useUpdate } from '@refinedev/core';
import { QueryObserverResult } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { IncomeFrequency } from './useIncomes';
import { useLocalStorage } from '@mantine/hooks';
import {
  Frequency,
  useTotalForFrequency,
  calculateAnnualAmount,
  calculateMonthlyAmount,
} from '../utils';
import { Dispatch, SetStateAction } from 'react';

export type ExpenditureCategory =
  | 'Insurance'
  | 'Cable'
  | 'Online'
  | 'Service'
  | 'Other bills'
  | 'Telephone'
  | 'Utilities'
  | 'Office supplies'
  | 'Advertising'
  | 'Business'
  | 'Postage'
  | 'Printing'
  | 'Office maintenance'
  | 'Wages'
  | 'ATM'
  | 'Entertainment'
  | 'Restaurants'
  | 'Hobbies'
  | 'Dues'
  | 'Gambling'
  | 'Charitable'
  | 'Gifts'
  | 'Groceries'
  | 'General'
  | 'Healthcare'
  | 'Personal'
  | 'Child'
  | 'Clothing'
  | 'Education'
  | 'Home Maintenance'
  | 'Home Improvement'
  | 'Pets'
  | 'Electronic'
  | 'Mixed'
  | 'Uncategorized'
  | 'Other'
  | 'Cheques'
  | 'Rent'
  | 'Loans'
  | 'Mortgages'
  | 'Taxes'
  | 'Automotive'
  | 'Gasoline'
  | 'Travel';

type ApiExpenditure = {
  id: string;
  description: string;
  category: ExpenditureCategory;
  amount: number;
  frequency: IncomeFrequency;
  clientId: string;
};

export type Expenditure = ApiExpenditure & {
  amountAnnually: number;
  amountMonthly: number;
};

export type EditableExpenditure = Omit<ApiExpenditure, 'id'> & {
  id?: string;
};

type UseExpendituresReturnType = QueryObserverResult<GetListResponse<ApiExpenditure>, HttpError> & {
  expendituresData: Expenditure[] | undefined;
  upsertExpenditures: (items: EditableExpenditure[]) => Promise<void>;
  deleteExpenditures: (ids: string[]) => Promise<void>;
  frequency: Frequency;
  setFrequency: Dispatch<SetStateAction<Frequency>>;
  total: number;
};

export default function useExpenditures(): UseExpendituresReturnType {
  const { clientId } = useCurrentClient();
  const [frequency, setFrequency] = useLocalStorage<Frequency>({
    key: 'cashflow-expenditures-frequency',
    defaultValue: 'monthly',
  });

  const result = useList<ApiExpenditure>({
    resource: clientId ? `clients/${clientId}/expenditures` : undefined,
  });
  const expendituresData = result.data?.data?.map(expenditure => ({
    ...expenditure,
    amountAnnually: calculateAnnualAmount(expenditure.amount, expenditure.frequency),
    amountMonthly: calculateMonthlyAmount(expenditure.amount, expenditure.frequency),
  }));

  const { mutateAsync: upsertExpendituresMutation } = useUpdate<EditableExpenditure>({
    resource: `clients/${clientId}/expenditures`,
    id: 'batch',
  });

  const upsertExpenditures = async (items: EditableExpenditure[]) => {
    await upsertExpendituresMutation({
      values: items.map(item => ({
        ...item,
        clientId,
      })),
    });

    await result.refetch();
  };

  const { mutateAsync: deleteExpendituresMutation } = useDeleteMany({});
  const deleteExpenditures = async (ids: string[]) => {
    await deleteExpendituresMutation({
      resource: `clients/${clientId}/expenditures`,
      ids,
    });
  };

  const total = useTotalForFrequency({
    items: expendituresData,
    frequency,
    frequencyField: 'frequency',
    amountField: 'amount',
  });

  return {
    expendituresData,
    upsertExpenditures,
    deleteExpenditures,
    frequency,
    setFrequency,
    total,
    ...result,
  };
}
